
'use client';

import {
  onSnapshot,
  doc,
  DocumentReference,
  FirestoreError,
  DocumentSnapshot,
} from 'firebase/firestore';
import { useState, useEffect, useMemo } from 'react';
import { useFirestore } from '../provider';
import { FirestorePermissionError } from '../errors';
import { errorEmitter } from '../error-emitter';

export const useMemoizedDoc = (docCreator: () => DocumentReference | null) => {
  const firestore = useFirestore();
  const docCreatorString = docCreator.toString();
  return useMemo(() => {
    if (!firestore) return null;
    return docCreator();
  }, [firestore, docCreatorString]);
};


export function useDoc<T>(ref: DocumentReference | null) {
  const firestore = useFirestore();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<FirestoreError | null>(null);

  useEffect(() => {
    if (ref && firestore) {
      setIsLoading(true);
      const unsubscribe = onSnapshot(
        ref,
        (snapshot: DocumentSnapshot) => {
          if (snapshot.exists()) {
            setData({ id: snapshot.id, ...snapshot.data() } as T);
          } else {
            setData(null);
          }
          setIsLoading(false);
        },
        (error: FirestoreError) => {
          try {
             const permissionError = new FirestorePermissionError({
                path: ref.path,
                operation: 'get',
            });
            errorEmitter.emit('permission-error', permissionError);
          } catch(e) {
            errorEmitter.emit('permission-error', error);
          }
          setError(error);
          setIsLoading(false);
        }
      );
      return () => unsubscribe();
    } else {
      setData(null);
      setIsLoading(false);
    }
  }, [ref, firestore]);

  return { data, isLoading, error };
}
